import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';

export type ChunkedUploadPurpose = 'temp' | 'attachment';

export interface ChunkedUploadOptions {
  purpose: ChunkedUploadPurpose;
  note?: string;
  category?: string;
  categoryother?: string;
  requirementId?: number;
  /** 0–100 */
  onProgress?: (percent: number, fileName: string) => void;
  /** Si retorna true, se aborta la sesión en el servidor */
  shouldCancel?: () => boolean;
}

export interface ChunkedUploadResult {
  id: number;
  purpose: ChunkedUploadPurpose;
  filename: string;
  storageKey: string;
}

const DEFAULT_PART_SIZE = 5 * 1024 * 1024;
const MAX_UPLOAD_BYTES = 10737418240; // 10 GB

@Injectable({
  providedIn: 'root'
})
export class ChunkedUploadService {
  private activeSessionIds = new Set<string>();

  constructor(private http: HttpClient) {}

  get hasActiveUploads(): boolean {
    return this.activeSessionIds.size > 0;
  }

  async uploadFile(file: File, options: ChunkedUploadOptions): Promise<ChunkedUploadResult> {
    if (!file || typeof (file as any).size !== 'number' || !(file as any).name) {
      throw new Error('Archivo inválido');
    }
    if (file.size <= 0) {
      throw new Error('El archivo está vacío');
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      throw new Error('El archivo supera el máximo permitido de 10 GB');
    }

    const init = await this.http.post<any>(
      `${environment.apiUrl}/trackvision/uploads/init`,
      {
        filename: file.name,
        totalSize: file.size,
        purpose: options.purpose,
        note: options.note || '',
        category: options.category || null,
        categoryother: options.categoryother || null,
        requirementId: options.requirementId || null,
        partSize: DEFAULT_PART_SIZE
      }
    ).toPromise();

    if (!init || init.error) {
      throw new Error(init?.error?.message || 'No se pudo iniciar la carga');
    }

    let sessionId: string | null = init.result.sessionId;
    const partSize: number = init.result.partSize || DEFAULT_PART_SIZE;
    this.activeSessionIds.add(sessionId);

    try {
      if (options.shouldCancel?.()) {
        await this.abort(sessionId);
        throw new Error('Carga cancelada');
      }

      const parts: { partNumber: number; etag: string }[] = [];
      const totalParts = Math.max(1, Math.ceil(file.size / partSize));

      for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
        if (options.shouldCancel?.()) {
          await this.abort(sessionId);
          throw new Error('Carga cancelada');
        }

        const start = (partNumber - 1) * partSize;
        const end = Math.min(start + partSize, file.size);
        const blob = file.slice(start, end);
        // Content-Range: bytes inicio-fin/total (fin es inclusivo)
        const contentRange = `bytes ${start}-${end - 1}/${file.size}`;

        const partResp = await this.putPartWithRetry(sessionId, partNumber, blob, contentRange);
        if (!partResp || partResp.error) {
          throw new Error(partResp?.error?.message || `Error al subir el fragmento ${partNumber}`);
        }
        parts.push({
          partNumber: partResp.result.partNumber,
          etag: partResp.result.etag
        });

        const percent = Math.min(99, Math.round((end / file.size) * 100));
        options.onProgress?.(percent, file.name);
      }

      const complete = await this.http.post<any>(
        `${environment.apiUrl}/trackvision/uploads/${sessionId}/complete`,
        { parts }
      ).toPromise();

      if (!complete || complete.error) {
        throw new Error(complete?.error?.message || 'No se pudo completar la carga');
      }

      // Evitar abort posterior: la sesión ya se cerró en complete
      const finishedSessionId = sessionId;
      this.activeSessionIds.delete(finishedSessionId);
      sessionId = null;

      options.onProgress?.(100, file.name);
      return complete.result as ChunkedUploadResult;
    } catch (err) {
      if (sessionId) {
        try {
          await this.abort(sessionId);
        } catch {
          // ignore abort errors
        }
        this.activeSessionIds.delete(sessionId);
      }
      throw err;
    }
  }

  /**
   * Sube varios archivos en secuencia (temp). Devuelve [{ id }] compatible con create requirement.
   */
  async uploadFilesAsTemp(
    files: Array<File & { note?: string; category?: string; categoryother?: string }>,
    onProgress?: (percent: number, fileName: string, fileIndex: number, fileCount: number) => void,
    shouldCancel?: () => boolean
  ): Promise<Array<{ id: number }>> {
    const results: Array<{ id: number }> = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const uploaded = await this.uploadFile(file, {
        purpose: 'temp',
        note: file.note || '',
        category: file.category || '',
        categoryother: file.categoryother || '',
        shouldCancel,
        onProgress: (percent, fileName) => {
          // No mostrar 100% global hasta que el último archivo termine de verdad
          const doneRatio = (i + (percent / 100)) / files.length;
          const overall = percent >= 100 && i === files.length - 1
            ? 100
            : Math.min(99, Math.round(doneRatio * 100));
          onProgress?.(overall, fileName, i, files.length);
        }
      });
      results.push({ id: uploaded.id });
    }
    return results;
  }

  async abort(sessionId: string): Promise<void> {
    await this.http.delete(
      `${environment.apiUrl}/trackvision/uploads/${sessionId}`
    ).toPromise();
  }

  /** Aborta todas las sesiones multipart activas (cancelación explícita del usuario). */
  async abortActiveUploads(): Promise<void> {
    const sessionIds = Array.from(this.activeSessionIds);
    for (const sessionId of sessionIds) {
      try {
        await this.abort(sessionId);
      } catch {
        // sesión ya cerrada / complete previo
      }
      this.activeSessionIds.delete(sessionId);
    }
  }

  private async putPartWithRetry(
    sessionId: string,
    partNumber: number,
    blob: Blob,
    contentRange: string,
    retries = 3
  ): Promise<any> {
    let lastError: any;
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        return await this.http.put(
          `${environment.apiUrl}/trackvision/uploads/${sessionId}/parts/${partNumber}`,
          blob,
          {
            headers: new HttpHeaders({
              'Content-Type': 'application/octet-stream',
              'Content-Range': contentRange
            })
          }
        ).toPromise();
      } catch (err) {
        lastError = err;
        if (attempt < retries) {
          await new Promise((r) => setTimeout(r, 500 * attempt));
        }
      }
    }
    throw lastError;
  }
}
